// iter63 — Inbox page: Notifications + Messages (with thread + reply) + Pages received
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Inbox as InboxIcon, Bell, MessageSquare, Megaphone, Trash2, Reply, Send,
  CheckCheck, Plus, X, Search, ArrowLeft, Clock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const ROLE_LABEL = {
  Admin: 'Admin', InventoryManager: 'Inventory Manager',
  ProductionSupervisor: 'Supervisor', Purchasing: 'Purchasing', Operator: 'Operator',
};

function api() {
  const token = localStorage.getItem('token');
  return axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} });
}

const formatTs = (ts) => {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const now = new Date();
    const ms = now - d;
    if (ms < 60_000) return 'just now';
    if (ms < 3600_000) return `${Math.floor(ms / 60000)}m ago`;
    if (ms < 86400_000) return `${Math.floor(ms / 3600000)}h ago`;
    if (ms < 7 * 86400_000) return `${Math.floor(ms / 86400000)}d ago`;
    return d.toLocaleString();
  } catch (_) { return ts; }
};

export default function InboxPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = new URLSearchParams(location.search).get('tab') || 'messages';
  const [tab, setTab] = useState(initialTab);

  // Notifications
  const [notifs, setNotifs] = useState([]);
  // Messages
  const [folder, setFolder] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [openMsg, setOpenMsg] = useState(null); // {message, thread}
  const [loading, setLoading] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [search, setSearch] = useState('');
  // iter78c — Pages (PA history)
  const [pages, setPages] = useState([]);
  const [openPage, setOpenPage] = useState(null);

  const loadNotifs = useCallback(async () => {
    try {
      const r = await api().get('/notifications');
      setNotifs(r.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api().get(`/messages?folder=${folder}`);
      setMessages(r.data || []);
    } catch (e) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [folder]);

  const loadPages = useCallback(async () => {
    try {
      const r = await api().get('/audio/my-pages?limit=200');
      setPages(r.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadNotifs(); }, [loadNotifs]);
  useEffect(() => { loadMessages(); }, [loadMessages]);
  useEffect(() => { loadPages(); }, [loadPages]);

  // Listen for realtime new messages via existing user WS bridge
  useEffect(() => {
    const handler = (ev) => {
      const t = ev.detail?.type;
      if (t === 'message:new') {
        loadMessages();
      } else if (t === 'notification') {
        loadNotifs();
      } else if (t === 'pa:tts' || t === 'pa:voice_invite') {
        loadPages();
        loadNotifs();
      }
    };
    window.addEventListener('app:ws_event', handler);
    return () => window.removeEventListener('app:ws_event', handler);
  }, [loadMessages, loadNotifs, loadPages]);

  const filteredMsgs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return messages;
    return messages.filter(m =>
      (m.subject || '').toLowerCase().includes(q)
      || (m.body || '').toLowerCase().includes(q)
      || (m.from_name || '').toLowerCase().includes(q)
    );
  }, [messages, search]);

  const filteredNotifs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return notifs;
    return notifs.filter(n =>
      (n.title || '').toLowerCase().includes(q)
      || (n.message || '').toLowerCase().includes(q)
    );
  }, [notifs, search]);

  const filteredPages = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pages;
    return pages.filter(p =>
      (p.text || '').toLowerCase().includes(q)
      || (p.from_name || '').toLowerCase().includes(q)
    );
  }, [pages, search]);

  const markPageSeen = async (pid) => {
    try {
      await api().post(`/audio/my-pages/${pid}/seen`);
      loadPages();
      loadNotifs();
    } catch (e) { /* ignore */ }
  };

  const markAllPagesSeen = async () => {
    try {
      await api().post('/audio/my-pages/mark-all-seen');
      loadPages();
      loadNotifs();
      toast.success('All pages marked as seen');
    } catch (e) { /* ignore */ }
  };

  // iter78k — per-row + bulk delete for pages
  const deletePage = async (pid) => {
    try {
      await api().delete(`/audio/my-pages/${pid}`);
      loadPages();
      loadNotifs();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const clearAllPages = async () => {
    if (!window.confirm('Delete every page from your inbox? This cannot be undone.')) return;
    try {
      const r = await api().delete('/audio/my-pages');
      loadPages();
      loadNotifs();
      toast.success(`Cleared ${r.data?.cleared ?? 0} page(s)`);
    } catch (e) {
      toast.error('Clear failed');
    }
  };

  const openMessage = async (msg) => {
    try {
      const r = await api().get(`/messages/${msg.id}`);
      setOpenMsg(r.data);
      if (r.data?.message?.is_unread) {
        await api().post(`/messages/${msg.id}/read`).catch(() => {});
        loadMessages();
      }
    } catch (e) {
      toast.error('Failed to open message');
    }
  };

  const deleteMsg = async (mid) => {
    try {
      await api().delete(`/messages/${mid}`);
      setOpenMsg(null);
      loadMessages();
      toast.success('Deleted');
    } catch (e) { toast.error('Delete failed'); }
  };

  const markAllRead = async () => {
    try {
      await api().post('/messages/mark-all-read');
      loadMessages();
      toast.success('All marked as read');
    } catch (e) { /* ignore */ }
  };

  const markAllNotifsRead = async () => {
    try {
      await api().put('/notifications/mark-all-read');
      loadNotifs();
    } catch (e) { /* ignore */ }
  };

  const markNotifRead = async (id) => {
    try {
      await api().put(`/notifications/${id}/read`);
      loadNotifs();
    } catch (e) { /* ignore */ }
  };

  const tabChange = (v) => {
    setTab(v);
    navigate(`?tab=${v}`, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <InboxIcon className="h-7 w-7 text-blue-600" />
            Inbox
          </h1>
          <p className="text-sm text-slate-500 mt-1">All your notifications, messages, and pages — in one place.</p>
        </div>
        <Button onClick={() => setComposeOpen(true)} data-testid="inbox-new-message-btn">
          <Plus className="h-4 w-4 mr-2" /> New Message
        </Button>
      </div>

      <Tabs value={tab} onValueChange={tabChange} className="w-full">
        <TabsList data-testid="inbox-tabs">
          <TabsTrigger value="messages" data-testid="inbox-tab-messages">
            <MessageSquare className="h-4 w-4 mr-2" /> Messages
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="inbox-tab-notifications">
            <Bell className="h-4 w-4 mr-2" /> Notifications
          </TabsTrigger>
          <TabsTrigger value="pages" data-testid="inbox-tab-pages">
            <Megaphone className="h-4 w-4 mr-2" /> Pages
            {pages.some(p => p.unread) && (
              <Badge className="ml-2 bg-red-500 text-white" data-testid="inbox-pages-unread-count">
                {pages.filter(p => p.unread).length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <div className="mt-3 flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              className="pl-8"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="inbox-search"
            />
          </div>
          {tab === 'messages' && (
            <>
              <Select value={folder} onValueChange={setFolder}>
                <SelectTrigger className="w-36" data-testid="inbox-folder-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inbox">Inbox</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="unread">Unread</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={markAllRead} data-testid="inbox-mark-all-read">
                <CheckCheck className="h-4 w-4 mr-2" /> Mark all read
              </Button>
            </>
          )}
          {tab === 'notifications' && (
            <Button variant="outline" onClick={markAllNotifsRead} data-testid="notifs-mark-all-read">
              <CheckCheck className="h-4 w-4 mr-2" /> Mark all read
            </Button>
          )}
          {tab === 'pages' && (
            <>
              <Button variant="outline" onClick={markAllPagesSeen} data-testid="pages-mark-all-seen">
                <CheckCheck className="h-4 w-4 mr-2" /> Mark all seen
              </Button>
              <Button variant="outline" onClick={clearAllPages} data-testid="pages-clear-all" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                Clear all
              </Button>
            </>
          )}
        </div>

        <TabsContent value="messages" className="mt-4">
          {loading && <div className="text-sm text-slate-500 py-4">Loading…</div>}
          {!loading && filteredMsgs.length === 0 && (
            <div className="border rounded-lg p-12 text-center text-slate-500" data-testid="inbox-empty">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <div>No messages in this folder.</div>
              <Button className="mt-4" onClick={() => setComposeOpen(true)} variant="outline">
                <Plus className="h-4 w-4 mr-2" /> Compose your first message
              </Button>
            </div>
          )}
          <div className="border rounded-lg divide-y bg-white">
            {filteredMsgs.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => openMessage(m)}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 flex items-start gap-3 ${m.is_unread ? 'bg-blue-50/40' : ''}`}
                data-testid={`inbox-msg-${m.id}`}
              >
                <div className="mt-0.5">
                  {m.is_unread
                    ? <span className="h-2 w-2 rounded-full bg-blue-600 inline-block" />
                    : <span className="h-2 w-2 rounded-full bg-slate-200 inline-block" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className={`text-sm ${m.is_unread ? 'font-semibold' : ''} truncate flex-1`}>
                      {m.is_mine ? `To: ${(m.recipients || []).length} recipient(s)` : m.from_name}
                    </div>
                    <div className="text-xs text-slate-500 whitespace-nowrap"><Clock className="inline h-3 w-3 mr-1" />{formatTs(m.sent_at)}</div>
                  </div>
                  <div className={`text-sm ${m.is_unread ? 'font-medium' : 'text-slate-700'} truncate mt-0.5`}>{m.subject || '(no subject)'}</div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">{(m.body || '').slice(0, 140)}</div>
                </div>
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="notifications" className="mt-4">
          {filteredNotifs.length === 0 && (
            <div className="border rounded-lg p-12 text-center text-slate-500" data-testid="notifs-empty">
              <Bell className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <div>No notifications.</div>
            </div>
          )}
          <div className="border rounded-lg divide-y bg-white">
            {filteredNotifs.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => { markNotifRead(n.id); if (n.link) navigate(n.link); }}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 flex items-start gap-3 ${!n.read ? 'bg-amber-50/40' : ''}`}
                data-testid={`notif-${n.id}`}
              >
                <div className="mt-0.5">
                  {!n.read
                    ? <span className="h-2 w-2 rounded-full bg-amber-600 inline-block" />
                    : <span className="h-2 w-2 rounded-full bg-slate-200 inline-block" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className={`text-sm ${!n.read ? 'font-semibold' : ''} truncate flex-1`}>{n.title}</div>
                    <div className="text-xs text-slate-500 whitespace-nowrap">{formatTs(n.created_at)}</div>
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</div>
                </div>
              </button>
            ))}
          </div>
        </TabsContent>

        {/* iter78c — Pages history (delivered + missed) */}
        <TabsContent value="pages" className="mt-4">
          {filteredPages.length === 0 && (
            <div className="border rounded-lg p-12 text-center text-slate-500" data-testid="pages-empty">
              <Megaphone className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <div>No pages received yet.</div>
            </div>
          )}
          <div className="border rounded-lg divide-y bg-white">
            {filteredPages.map((p) => {
              const missed = p.missed_offline;
              const unread = p.unread;
              const priorityColor =
                p.priority === 'emergency' ? 'bg-red-600 text-white' :
                p.priority === 'urgent' ? 'bg-amber-500 text-white' :
                'bg-slate-300 text-slate-700';
              return (
                <div
                  key={p.id}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 flex items-start gap-3 ${
                    unread ? (missed ? 'bg-red-50/60' : 'bg-blue-50/40') : ''
                  }`}
                  data-testid={`inbox-page-${p.id}`}
                >
                  <button
                    type="button"
                    onClick={() => { setOpenPage(p); if (unread) markPageSeen(p.id); }}
                    className="flex items-start gap-3 flex-1 text-left"
                  >
                    <div className="mt-0.5">
                      {unread
                        ? <span className={`h-2 w-2 rounded-full inline-block ${missed ? 'bg-red-600' : 'bg-blue-600'}`} />
                        : <span className="h-2 w-2 rounded-full bg-slate-200 inline-block" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className={`text-sm ${unread ? 'font-semibold' : ''} truncate flex-1`}>
                          {p.from_name || 'Unknown sender'}
                          {p.from_role && <span className="text-xs text-slate-500 ml-1">({ROLE_LABEL[p.from_role] || p.from_role})</span>}
                        </div>
                        <Badge className={`text-[10px] uppercase tracking-wide ${priorityColor}`}>{p.priority || 'normal'}</Badge>
                        {missed && <Badge className="bg-red-100 text-red-700 text-[10px]">Missed — you were offline</Badge>}
                        <div className="text-xs text-slate-500 whitespace-nowrap"><Clock className="inline h-3 w-3 mr-1" />{formatTs(p.ts)}</div>
                      </div>
                      <div className={`text-sm ${unread ? 'font-medium' : 'text-slate-700'} mt-0.5 line-clamp-2`}>
                        {p.text || (p.mode === 'voice' ? '(voice page — no text recorded)' : '(no text)')}
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); deletePage(p.id); }}
                    className="text-slate-400 hover:text-red-600 px-2 self-start"
                    title="Delete this page from your inbox"
                    data-testid={`inbox-page-delete-${p.id}`}
                    aria-label="Delete"
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Message detail dialog (with reply) */}
      {openMsg && (
        <MessageDetail
          data={openMsg}
          onClose={() => setOpenMsg(null)}
          onDelete={deleteMsg}
          onSent={() => { loadMessages(); openMessage({ id: openMsg.message.id }); }}
        />
      )}

      {/* iter78c — Page detail dialog (with iter78m reply UI) */}
      {openPage && (
        <Dialog open={true} onOpenChange={() => setOpenPage(null)}>
          <DialogContent data-testid="page-detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-orange-500" />
                Page from {openPage.from_name || 'Unknown sender'}
              </DialogTitle>
              <DialogDescription>
                {formatTs(openPage.ts)} • Priority: <strong className="uppercase">{openPage.priority || 'normal'}</strong>
                {openPage.missed_offline && (
                  <span className="ml-2 inline-block bg-red-100 text-red-700 text-[10px] px-2 py-0.5 rounded">Missed — you were offline</span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="bg-slate-50 rounded-md p-4 text-sm whitespace-pre-wrap">
              {openPage.text || (openPage.mode === 'voice' ? '(Voice page — text was not transcribed.)' : '(No text)')}
            </div>
            {/* iter78m — quick reply directly from the page detail */}
            {openPage.from_user_id && (
              <PageReplyForm
                api={api}
                fromName={openPage.from_name}
                fromUserId={openPage.from_user_id}
                replyToId={openPage.id}
                onSent={() => setOpenPage(null)}
              />
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenPage(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <ComposeDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={() => { setComposeOpen(false); loadMessages(); toast.success('Message sent'); }}
      />
    </div>
  );
}

function MessageDetail({ data, onClose, onDelete, onSent }) {
  const { message, thread } = data || {};
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await api().post(`/messages/${message.id}/reply`, { body: replyText.trim() });
      setReplyText('');
      setReplyOpen(false);
      onSent();
      toast.success('Reply sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reply failed');
    } finally { setSending(false); }
  };
  if (!message) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="message-detail-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="text-xl">{message.subject || '(no subject)'}</DialogTitle>
            <Button size="sm" variant="ghost" onClick={() => onDelete(message.id)} data-testid="msg-delete-btn">
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
          <DialogDescription>Thread of {(thread || []).length} message(s).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(thread || []).map((m) => (
            <div key={m.id} className={`p-3 rounded border ${m.is_mine ? 'bg-slate-50' : 'bg-blue-50/40'}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-sm font-semibold">{m.from_name}</div>
                <div className="text-xs text-slate-500">{formatTs(m.sent_at)}</div>
              </div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{m.body}</div>
            </div>
          ))}
        </div>
        <DialogFooter className="flex justify-between items-center">
          <Button variant="outline" onClick={onClose} data-testid="msg-close-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Close
          </Button>
          {!replyOpen ? (
            <Button onClick={() => setReplyOpen(true)} data-testid="msg-reply-btn">
              <Reply className="h-4 w-4 mr-2" /> Reply
            </Button>
          ) : null}
        </DialogFooter>
        {replyOpen && (
          <div className="border-t pt-3 space-y-2">
            <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={4} placeholder="Type your reply…" data-testid="msg-reply-text" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setReplyOpen(false)}><X className="h-4 w-4 mr-1" /> Cancel</Button>
              <Button size="sm" disabled={sending || !replyText.trim()} onClick={sendReply} data-testid="msg-reply-send">
                <Send className="h-4 w-4 mr-1" />{sending ? 'Sending…' : 'Send Reply'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ComposeDialog({ open, onClose, onSent, prefilledRecipient }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api().get('/audio/pageable-users');
        setUsers(r.data || []);
      } catch (e) { /* ignore */ }
    })();
    setSelected(prefilledRecipient ? [prefilledRecipient] : []);
    setSubject('');
    setBody('');
    setSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefilledRecipient]);

  const toggle = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u => (u.name || '').toLowerCase().includes(q) || (u.role || '').toLowerCase().includes(q));
  }, [users, search]);

  const send = async () => {
    if (selected.length === 0) { toast.error('Pick at least one recipient'); return; }
    if (!body.trim()) { toast.error('Write a message'); return; }
    setSending(true);
    try {
      const r = await api().post('/messages', { recipients: selected, subject: subject.trim(), body: body.trim() });
      toast.success(`Sent to ${r.data.delivered} recipient(s)${r.data.blocked ? ` (${r.data.blocked} blocked by their prefs)` : ''}`);
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="compose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> New Message</DialogTitle>
          <DialogDescription>Send a note to one or more teammates. Honors their permission/DND settings.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs">Recipients ({selected.length} selected)</label>
            <div className="relative mt-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input className="pl-7" placeholder="Search teammates" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="compose-user-search" />
            </div>
            <div className="border rounded mt-1 max-h-44 overflow-y-auto divide-y">
              {filtered.map(u => (
                <label key={u.id} className={`flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 cursor-pointer ${!u.can_page ? 'opacity-50' : ''}`} data-testid={`compose-user-${u.id}`}>
                  <input type="checkbox" checked={selected.includes(u.id)} disabled={!u.can_page} onChange={() => toggle(u.id)} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{u.name}</div>
                    <div className="text-[10px] text-slate-500">{ROLE_LABEL[u.role] || u.role}</div>
                  </div>
                  {u.online
                    ? <Badge variant="outline" className="bg-emerald-50 border-emerald-300 text-emerald-700 text-[10px]">online</Badge>
                    : <Badge variant="outline" className="text-slate-400 text-[10px]">offline</Badge>}
                </label>
              ))}
              {filtered.length === 0 && <div className="text-xs text-slate-500 p-3 text-center">No users.</div>}
            </div>
          </div>
          <div>
            <label className="text-xs">Subject (optional)</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} data-testid="compose-subject" />
          </div>
          <div>
            <label className="text-xs">Message</label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} maxLength={5000} data-testid="compose-body" />
            <div className="text-[10px] text-slate-500 text-right">{body.length}/5000</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={sending} onClick={send} data-testid="compose-send-btn">
            <Send className="h-4 w-4 mr-2" />{sending ? 'Sending…' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PageReplyForm({ api, fromName, fromUserId, replyToId, onSent }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api().post('/audio/page', {
        recipients: [fromUserId],
        mode: 'tts',
        text: text.trim(),
        priority: 'normal',
        reply_to: replyToId || null, // iter78m — bypass allow_roles on original sender
      });
      toast.success(`Reply sent to ${fromName || 'sender'}`);
      setText('');
      if (onSent) onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reply failed');
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="border-t border-slate-200 pt-3 mt-3 space-y-2" data-testid="page-detail-reply-form">
      <div className="text-xs uppercase tracking-wider text-slate-500">Reply with a page</div>
      <Textarea
        rows={2}
        maxLength={300}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`Quick reply to ${fromName || 'sender'}…`}
        data-testid="page-detail-reply-input"
      />
      <div className="flex justify-between items-center">
        <span className="text-[10px] text-slate-400">{text.length}/300</span>
        <Button
          size="sm"
          onClick={send}
          disabled={sending || !text.trim()}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="page-detail-reply-send"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />
          {sending ? 'Sending…' : 'Send reply page'}
        </Button>
      </div>
    </div>
  );
}

